import React from 'react';
import { useFragment } from 'react-relay/hooks';

import {
  Checkbox,
  createStyles,
  ListItem,
  ListItemIcon,
  ListItemText,
  Theme,
  Typography,
  withStyles,
  WithStyles,
} from '@material-ui/core';
import { ListItemProps } from '@material-ui/core/ListItem';
import { CheckBox as CheckBoxIcon, CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon } from '@material-ui/icons';
import { graphql } from 'babel-plugin-relay/macro';
import classnames from 'classnames';
import { ErrorBoundary } from 'core';
import { get } from 'lodash';

import { ListItemPatientChatActivityEmpty_Patient$key } from './__generated__/ListItemPatientChatActivityEmpty_Patient.graphql';
import AvatarNode from './AvatarNode';

const styles = ({
  custom: {
    palette: { colorSupportPowder, colorSupportSteel },
  },
}: Theme) =>
  createStyles({
    listItem: {
      borderBottom: '1px solid rgba(0, 0, 0, 0.25)',
      '&$selected': {
        backgroundColor: colorSupportPowder,
        '&:hover': {
          backgroundColor: colorSupportPowder,
        },
      },
    },
    selected: {},
    checkbox: {
      color: colorSupportSteel,
    },
  });

export interface ListItemPatientChatActivityEmptyProps
  extends Omit<ListItemProps, 'classes'>,
    WithStyles<typeof styles> {
  fragmentRef: ListItemPatientChatActivityEmpty_Patient$key;
}

const ListItemPatientChatActivityEmpty: React.FC<ListItemPatientChatActivityEmptyProps> = ({
  classes,
  fragmentRef,
  selected,
  ...rest
}) => {
  const patient = useFragment<ListItemPatientChatActivityEmpty_Patient$key>(
    graphql`
      fragment ListItemPatientChatActivityEmpty_Patient on Patient {
        ...AvatarNode_Node
        firstname
        lastname
      }
    `,
    fragmentRef,
  );

  const lastname = get(patient, 'lastname') ? `${patient.lastname}, ` : '';
  const firstname = get(patient, 'firstname', '');
  const fullName = lastname + firstname;

  return (
    <ListItem
      disableGutters
      className={classnames(classes.listItem, {
        [classes.selected]: selected,
      })}
      {...rest}
    >
      <Checkbox
        color="primary"
        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
        checkedIcon={<CheckBoxIcon fontSize="small" />}
        className={classes.checkbox}
        disabled
        inputProps={{ 'data-lh-id': 'list-item-patient-chat-activity-empty-checkbox' }}
      />
      <ListItemIcon>
        <AvatarNode fragmentRef={patient} />
      </ListItemIcon>
      <ListItemText
        disableTypography
        primary={<Typography>{fullName}</Typography>}
        secondary={
          <Typography variant="body2" color="textSecondary" noWrap>
            <i>No recent activity</i>
          </Typography>
        }
      />
    </ListItem>
  );
};

export default withStyles(styles)((props: ListItemPatientChatActivityEmptyProps) => (
  <ErrorBoundary fallback={null}>
    <ListItemPatientChatActivityEmpty {...props} />
  </ErrorBoundary>
));
